import {
  Organization,
  OrganizationMember,
  OrganizationRole,
  OrganizationRoleBinding,
  Permission,
  UserPrivate,
} from '@pollination-solutions/pollination-sdk'
import { BaseService } from './base'
import { OrganizationRoleEnum } from './types'
import { APIClient } from 'auth-context'

export type OrganizationWithPermissions = Organization & {
  roles: {
    permissions: { [key: string]: Permission } | null
    roleName: OrganizationRoleEnum
    roleDescription: string
  }[]
}

export type OrganizationMemberWithRoles = OrganizationMember & {
  roles: OrganizationRole[]
}

export type SDKState = {
  orgs: OrganizationWithPermissions[]
  members: OrganizationMemberWithRoles[]
}

/**
 * Service to manage SDK-related state and actions
 */
class SDKService extends BaseService<SDKState> {
  // TODO: Move client and user to a service
  private client: APIClient  | null = null
  private user: UserPrivate | null = null

  setClient(client: APIClient) {
    this.client = client
  }

  setUser(user: UserPrivate) {
    this.user = user
  }

  /**
   * Fetch organizations the user is a member of along with their roles
   * @returns Promise<void>
   */
  async fetchOrganizations() {
    if (!this.client || !this.user) {
      this.updateState({ orgs: [] })
      return
    }

    try {
      const { data } = await this.client.orgs.listOrgs({
        member: [this.user.username],
        perPage: 10,
      })

      const orgs: OrganizationWithPermissions[] = data.resources.map(
        (item) =>
          ({
            ...item,
            roles: []
          }) as OrganizationWithPermissions
      )

      try {
        const { data: roles } = await this.client.user.getOrganizationRoles()
        orgs.forEach((org) => {
          const orgRoles = roles.filter((r) => r.owner.id === org.owner.id)
          orgRoles.forEach((role) => {
            if (role) {
              org.roles.push(
                {
                  permissions: role.resources ?? null,
                  roleName: role.name as OrganizationRoleEnum,
                  roleDescription: role.description,
                }
              )
            }
          })
          if (orgRoles.length === 0) {
            org.roles = [{
                permissions: null,
                roleName: OrganizationRoleEnum.Member,
                roleDescription: 'This role consumes licenses based on your organization\'s plan.',
              }]
          }
        })
        
        this.updateState({ orgs })
      } catch (error) {
        console.error('Error fetching organization roles:', error)
      }
    } catch (error) {
      console.error('Error fetching organizations:', error)
    }
  }

  /**
   * Fetch members of an organization along with their roles
   * TODO: Paginate if needed
   * @param orgName Organization name
   * @returns Promise<void>
   */
  async fetchMembers(orgName: string) {
    if (!this.client) {
      this.updateState({ members: [] })
      return
    }

    // Ensure organizations are loaded
    await this.fetchOrganizations()
    const org = this.getState().orgs.find((o) => o.account_name === orgName)

    if (!org) {
      this.updateState({ members: [] })
      return
    }

    // Only SuperAdmins can view members
    const names = org.roles.map((r) => r.roleName)
    if (!names.includes(OrganizationRoleEnum.SuperAdmin)) {
      this.updateState({ members: [] })
      return
    }

    try {
      const { data } = await this.client.orgs.getOrgMembers({
        name: orgName,
      })

      const members: OrganizationMemberWithRoles[] = data.resources.map(
        (member) => ({
          ...member,
          roles: [],
        })
      )

      try {
        const { data: roles } = await this.client.orgs.getOrgRoleBindings({
          name: orgName,
        })

        members.forEach((member) => {
          roles
            .filter((binding) => binding.user.username === member.user.username)
            .forEach((binding: OrganizationRoleBinding) => {
              if (binding.role) {
                member.roles.push(binding.role)
              }
            })
        })
      } catch (error) {
        console.error('Error fetching member roles:', error)
      }

      this.updateState({ members })
    } catch (error) {
      console.error('Error fetching organization members:', error)
      this.updateState({ members: [] })
    }
  }

  /**
   * Delete a member from an organization
   * @param orgName Organization name
   * @param username User name
   * @returns Promise<void>
   */
  async deleteMember(orgName: string, username: string) {
    if (!this.client) {
      return
    }

    try {
      await this.client.orgs.deleteOrgMember({
        name: orgName,
        username,
      })
      // Refresh members after deletion
      this.fetchMembers(orgName)
    } catch (error) {
      console.error('Error deleting organization member:', error)
    }
  }

  /**
   * Add a member to an organization
   * @param orgName Organization name
   * @param username User name
   * @returns Promise<void>
   */
  async addMember(orgName: string, username: string) {
    if (!this.client) {
      return
    }

    try {
      await this.client.orgs.addOrganizationMember({
        name: orgName,
        username,
      })
      // Refresh members after addition
      this.fetchMembers(orgName)
    } catch (error) {
      console.error('Error adding organization member:', error)
    }
  }

  /**
   * Clear the SDK state
   */
  clearState() {
    this.updateState({
      orgs: [],
      members: [],
    })
  }

  /**
   * SDKService constructor
   */
  constructor() {
    super()
    this.state = {
      orgs: [],
      members: [],
    }
  }
}

export const sdkService = new SDKService()
